// src/lib/menage-suivi-aggregate.js
// CHANTIER 42 Phase 3 (ADR-230) — agrégation pure pour l'écran de PILOTAGE de
// l'équipe (orchestrateur-only). LECTURE PURE : aucun accès réseau, aucun DOM,
// aucune écriture. Alimente MenageSuiviView.
//
// MODÈLE (décisions Dimitri) : « Suivi équipe » = aperçu SEMAINE + détail
// AUJOURD'HUI + ALERTE « créneau du jour dépassé sans validation ». On raisonne
// sur la semaine courante [weekStart, weekEnd] + le jour J.
//
// COMPTEURS = 2 ÉTATS, SUR TOUTE LA SEMAINE (décision Dimitri 2026-08-06) :
//   fait   = status === completed.
//   aFaire = tout le reste de la semaine (scheduled, in_progress…).
//   skipped EXCLU de tous les compteurs (run annulé).
//
// POURQUOI 2 ET PAS 4. « Mon agent fait le ménage ou il ne l'a pas fait. » Les
// deux colonnes retirées ne disaient rien d'utile :
//   - « en cours » ne lisait PAS le statut `in_progress` : c'était une déduction
//     horaire (check-out dépassé ⇒ supposé en cours). Sur un logement sans
//     horaire connu, elle affichait 0 en permanence — ce qui était le cas de
//     TOUS les logements côté orchestrateur avant le lot « guichet ».
//   - « à venir » ne comptait que le reste de la semaine, ce qui obligeait
//     « à faire » et « faits » à ne compter QUE le jour J pour ne pas
//     double-compter. Le tableau s'appelait « cette semaine » et montrait la
//     journée. Les deux colonnes restantes couvrent désormais vraiment la
//     semaine, et le titre redevient vrai.
//
// CE QUI REMONTE À LA SURFACE, ET C'EST VOULU : un ménage COMMENCÉ mais jamais
// terminé (`in_progress`, posé dès la 1ʳᵉ case cochée par l'agent) n'est pas
// fait — il compte donc « à faire », y compris sur un jour PASSÉ de la semaine.
// Avant, les jours passés n'avaient aucun bucket et ces runs disparaissaient.
// La production en portait 3, figés depuis mai/juin (constat 2026-08-06).
//
// ⚠️ Le statut DB `in_progress` n'est PAS supprimé : il reste lu par l'écran
// admin Ménages, la fiche réservation, l'espace propriétaire, la clôture de
// journée et la policy d'assignation. Seules les COLONNES de cet écran partent.
//
// Les horaires théoriques restent utilisés — mais UNIQUEMENT pour l'ALERTE et
// le tri du jour, plus pour les compteurs.
//
// ALERTE (ADR-230) — calcul LOCAL, ne PAS appeler getEffectiveRunStatus :
//   alerte SSI check-in théorique connu (non-null) ET status ≠ completed ET
//   now ≥ check-in ET check-in > check-out (garde anti-données aberrantes).
//   Garde-fou minuit : check-in null → JAMAIS d'alerte (un logement sans horaire
//   ne déclenche pas d'alerte à minuit). L'alerte = « check-in dépassé sans
//   validation » (le voyageur suivant peut arriver sur un logement non fait).
//
// checkoutTimeByLog / checkinTimeByLog : FONCTIONS PURES injectées
// (logement_id) => "HH:MM"|null. Le module n'accède JAMAIS à cfg directement (le
// caller construit ces fonctions depuis cfg) ni au RÉEL voyageur (horaire
// THÉORIQUE config uniquement — Lot 3 réel protégé).
//
// Toutes les heures sont normalisées via parseHourString avant comparaison
// (robustesse "9:05" non-paddé, "11h", etc. — RISQUE C défensif).

import { parseHourString } from './menage-status-helpers'

function emptyCounters() {
  return { aFaire: 0, fait: 0 }
}

const COMPLETED = 'completed'
const SKIPPED = 'skipped'

/**
 * Agrège les runs d'un pool d'agents pour l'écran de pilotage orchestrateur.
 *
 * @param {Array<{ assigned_provider_email: string, status: string, scheduled_date: string, logement_id: string }>} runs
 *        Runs bruts (menage_runs) du pool sur la semaine. Tolère null/non-array → [].
 * @param {{ todayIso: string, weekStart: string, weekEnd: string, nowHHMM: string,
 *          checkoutTimeByLog: (logement_id: string) => (string|null),
 *          checkinTimeByLog: (logement_id: string) => (string|null) }} opts
 *        - todayIso  : date Paris du jour "YYYY-MM-DD".
 *        - weekStart : lundi de la semaine "YYYY-MM-DD".
 *        - weekEnd   : dimanche de la semaine "YYYY-MM-DD".
 *        - nowHHMM   : heure courante Paris "HH:MM".
 *        - checkoutTimeByLog : fonction pure logement_id → "HH:MM"|null (théorique).
 *        - checkinTimeByLog  : fonction pure logement_id → "HH:MM"|null (théorique).
 * @returns {{
 *   weekByAgent: Array<{ email: string, aFaire: number, fait: number }>,
 *   todayRuns: Array<object>,
 *   alerts: Array<object>
 * }}
 *   - weekByAgent : un objet par `assigned_provider_email` distinct, compteurs sur
 *     [weekStart, weekEnd] — 2 états seulement (fait / à faire), horaires non
 *     utilisés. Ordre = première apparition dans `runs` (déterministe).
 *   - todayRuns : runs du jour (scheduled_date === todayIso, hors skipped).
 *   - alerts : runs du jour dont le CHECK-IN théorique est franchi sans validation.
 */
export function aggregateSuivi(
  runs,
  { todayIso, weekStart, weekEnd, nowHHMM, checkoutTimeByLog, checkinTimeByLog } = {}
) {
  const list = Array.isArray(runs) ? runs : []
  const checkoutOf =
    typeof checkoutTimeByLog === 'function' ? checkoutTimeByLog : () => null
  const checkinOf =
    typeof checkinTimeByLog === 'function' ? checkinTimeByLog : () => null

  // now normalisé une fois (RISQUE C : "9:05" non-paddé géré par parseHourString).
  const now = parseHourString(nowHHMM)

  // weekByAgent indexé par email pour l'agrégation, + ordre d'apparition stable.
  const byEmail = new Map()
  const order = []
  const todayRuns = []
  const alerts = []

  for (const run of list) {
    const status = run?.status || 'scheduled'
    const sd = run?.scheduled_date || ''

    // skipped EXCLU partout (run annulé).
    if (status === SKIPPED) continue

    const email = run?.assigned_provider_email || '(non assigné)'
    if (!byEmail.has(email)) {
      byEmail.set(email, { email, ...emptyCounters() })
      order.push(email)
    }
    const counters = byEmail.get(email)

    const isToday = sd === todayIso
    const inWeek = sd >= String(weekStart || '') && sd <= String(weekEnd || '')

    // ── COMPTEURS : 2 états, sur TOUTE la semaine (hors skipped, déjà exclu) ──
    // Aucun horaire ici : un ménage est fait, ou il ne l'est pas.
    if (inWeek) {
      if (status === COMPLETED) counters.fait += 1
      else counters.aFaire += 1
    }

    if (isToday) {
      const checkout = parseHourString(checkoutOf(run?.logement_id))
      const checkin = parseHourString(checkinOf(run?.logement_id))

      // Détail du jour.
      todayRuns.push(run)

      // ── ALERTE : check-in théorique FRANCHI sans validation (calcul LOCAL) ──
      // Gardes : check-in connu (anti-minuit) · non completed · now connu ·
      // now ≥ check-in · check-in > check-out (anti données aberrantes).
      if (
        checkin &&
        status !== COMPLETED &&
        now &&
        now >= checkin &&
        (!checkout || checkin > checkout)
      ) {
        alerts.push(run)
      }
    }
  }

  return {
    weekByAgent: order.map((email) => byEmail.get(email)),
    todayRuns,
    alerts,
  }
}
